import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, RotateCcw, Map as MapIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DataTableColumnHeader,
  DataTablePagination,
} from '@/components/data-table'
import { useAircraftsRegistryStore } from '@/stores/aircraftsRegistryStore'
import { useAppContext } from '@/hooks/routing'
import type {
  AircraftTableRow,
  AircraftStatus,
  GroupingOption,
  OnFieldStatus,
  PresenceFilter,
  CrmStageFilter,
  AircraftRelationship,
  SavedViewId,
  ContextualColumnId,
} from '@/types/aircrafts'
import {
  AIRCRAFT_STATUS_LABELS,
  ON_FIELD_STATUS_CONFIG,
  PRESENCE_OPTIONS,
  RELATIONSHIP_LABELS,
  CRM_STAGE_OPTIONS,
  getSavedViewConfig,
  getCategoryIconGroup,
  type CategoryIconGroup,
} from '@/types/aircrafts'

interface AircraftTableProps {
  data: AircraftTableRow[]
  isLoading: boolean
  activeView: SavedViewId
  onEdit: (id: string) => void
  onDelete: (id: string, tailNumber: string) => void
  onRestore?: (id: string) => void
  onRowClick: (id: string) => void
}

/** Extended row type with registry fields */
type ExtendedRow = AircraftTableRow & {
  on_field_status?: OnFieldStatus | null
  presence?: PresenceFilter
  crm_stage?: CrmStageFilter
  building_name?: string | null
  unit_name?: string | null
  relationship?: AircraftRelationship | string | null
  operator_client_name?: string | null
  lease_contract_number?: string | null
  lease_end_date?: string | null
  lease_monthly_rate?: number | null
  arrived_at?: string | null
  scheduled_departure?: string | null
  days_on_field?: number | null
  allocation_id?: string | null
  deleted_at?: string | null
  visits_12mo?: number | null
  last_visit_date?: string | null
}

// ============================================
// Category Icon
// ============================================

const ICON_GROUP_LABELS: Record<CategoryIconGroup, string> = {
  jet: 'Jet',
  turboprop: 'Turboprop',
  piston: 'Piston',
  helo: 'Helicopter',
  unknown: '',
}

function CategoryIcon({ category }: { category: string | null }) {
  const group = getCategoryIconGroup(category)
  if (group === 'unknown') return null

  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-muted text-muted-foreground shrink-0"
      title={ICON_GROUP_LABELS[group]}
    >
      {group === 'jet' && 'J'}
      {group === 'turboprop' && 'T'}
      {group === 'piston' && 'P'}
      {group === 'helo' && 'H'}
    </span>
  )
}

// ============================================
// Grouping
// ============================================

function groupRows(
  rows: Row<AircraftTableRow>[],
  groupBy: GroupingOption
): Map<string, Row<AircraftTableRow>[]> {
  if (groupBy === 'none') {
    return new Map([['all', rows]])
  }

  const groups = new Map<string, Row<AircraftTableRow>[]>()

  rows.forEach((row) => {
    const original = row.original as ExtendedRow
    let groupKey: string

    switch (groupBy) {
      case 'status':
        groupKey = original.on_field_status || original.status || 'unknown'
        break
      case 'location':
        groupKey = original.building_name || original.home_location_name || 'Unassigned'
        break
      case 'relationship':
        groupKey = original.relationship || 'unknown'
        break
      case 'operator':
        groupKey = original.operator_client_name || original.owner_client_name || original.owner_name || 'Unknown'
        break
      case 'crm_stage':
        groupKey = original.crm_stage || 'unknown'
        break
      case 'aircraft_type': {
        const typeName = original.aircraft_type_name || (original.manufacturer && original.model ? `${original.manufacturer} ${original.model}` : null)
        groupKey = typeName || 'Unknown Type'
        break
      }
      default:
        groupKey = 'all'
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(row)
  })

  // Sort groups by predefined order for status
  if (groupBy === 'status') {
    const sortedGroups = new Map<string, Row<AircraftTableRow>[]>()
    const statusOrder = ['arriving', 'in_hangar', 'on_ramp', 'departing', 'active', 'maintenance', 'storage', 'departed', 'inactive', 'unknown']

    statusOrder.forEach((status) => {
      if (groups.has(status)) {
        sortedGroups.set(status, groups.get(status)!)
      }
    })

    groups.forEach((value, key) => {
      if (!sortedGroups.has(key)) {
        sortedGroups.set(key, value)
      }
    })

    return sortedGroups
  }

  // Sort CRM stage groups
  if (groupBy === 'crm_stage') {
    const sortedGroups = new Map<string, Row<AircraftTableRow>[]>()
    const stageOrder = ['active', 'lead', 'prospect', 'former', 'unknown']

    stageOrder.forEach((stage) => {
      if (groups.has(stage)) {
        sortedGroups.set(stage, groups.get(stage)!)
      }
    })

    groups.forEach((value, key) => {
      if (!sortedGroups.has(key)) {
        sortedGroups.set(key, value)
      }
    })

    return sortedGroups
  }

  return groups
}

function getGroupLabel(groupKey: string, groupBy: GroupingOption): { label: string; emoji?: string } {
  if (groupBy === 'status') {
    const onFieldConfig = ON_FIELD_STATUS_CONFIG[groupKey as OnFieldStatus]
    if (onFieldConfig) {
      return { label: onFieldConfig.label, emoji: onFieldConfig.emoji }
    }
    const statusLabel = AIRCRAFT_STATUS_LABELS[groupKey as AircraftStatus]
    if (statusLabel) {
      return { label: statusLabel }
    }
  }

  if (groupBy === 'crm_stage') {
    const stageOption = CRM_STAGE_OPTIONS.find((o) => o.value === groupKey)
    if (stageOption) {
      return { label: stageOption.label, emoji: stageOption.icon }
    }
  }

  if (groupBy === 'relationship') {
    const relLabel = RELATIONSHIP_LABELS[groupKey as AircraftRelationship]
    if (relLabel) {
      return { label: relLabel }
    }
  }

  return { label: groupKey }
}

// ============================================
// Contextual column builders
// ============================================

function buildContextualColumn(colId: ContextualColumnId): ColumnDef<AircraftTableRow> {
  switch (colId) {
    case 'arrived_at':
      return {
        id: 'arrived_at',
        header: 'Arrived',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          if (!original.arrived_at) {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <span className="text-sm">
              {new Date(original.arrived_at).toLocaleDateString()}
            </span>
          )
        },
      }

    case 'etd':
      return {
        id: 'etd',
        header: 'ETD',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          if (!original.scheduled_departure) {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <span className="text-sm">
              {new Date(original.scheduled_departure).toLocaleDateString()}
            </span>
          )
        },
      }

    case 'days_on_field':
      return {
        id: 'days_on_field',
        header: 'Days',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const days = original.days_on_field
          if (days == null) {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <Badge variant={days > 5 ? 'destructive' : 'secondary'} className="text-xs font-mono">
              {days}d
            </Badge>
          )
        },
      }

    case 'contract_number':
      return {
        id: 'contract_number',
        header: 'Contract',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          if (!original.lease_contract_number) {
            return <span className="text-muted-foreground">—</span>
          }
          return <span className="text-sm font-mono">{original.lease_contract_number}</span>
        },
      }

    case 'lease_expiry':
      return {
        id: 'lease_expiry',
        header: 'Lease Ends',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          if (!original.lease_end_date) {
            return <span className="text-muted-foreground">—</span>
          }

          const endDate = new Date(original.lease_end_date)
          const now = new Date()
          const daysUntil = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

          return (
            <span
              className={cn(
                'text-sm',
                daysUntil <= 30 ? 'text-orange-600 dark:text-orange-500 font-medium' : 'text-muted-foreground'
              )}
            >
              {endDate.toLocaleDateString()}
              {daysUntil > 0 && daysUntil <= 30 && ` (${daysUntil}d)`}
            </span>
          )
        },
      }

    case 'unit_assigned':
      return {
        id: 'unit_assigned',
        header: 'Unit',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const unit = original.unit_name
          if (!unit) {
            return <span className="text-muted-foreground">—</span>
          }
          return <span className="text-sm">{unit}</span>
        },
      }

    case 'visit_count_12mo':
      return {
        id: 'visit_count_12mo',
        header: 'Visits (12mo)',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const visits = original.visits_12mo
          if (visits == null) {
            return <span className="text-muted-foreground">—</span>
          }
          return <span className="text-sm font-mono">{visits}</span>
        },
      }

    case 'crm_stage':
      return {
        id: 'crm_stage_col',
        header: 'CRM Stage',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const stage = original.crm_stage ?? 'unknown'
          const option = CRM_STAGE_OPTIONS.find((o) => o.value === stage)
          return (
            <Badge variant="outline" className="text-xs">
              {option?.icon && <span className="mr-1">{option.icon}</span>}
              {option?.label ?? stage}
            </Badge>
          )
        },
      }

    case 'last_visit_date':
      return {
        id: 'last_visit_date',
        header: 'Last Visit',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          if (!original.last_visit_date) {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <span className="text-sm">
              {new Date(original.last_visit_date).toLocaleDateString()}
            </span>
          )
        },
      }
  }
}

// ============================================
// Main Component
// ============================================

export function AircraftTable({
  data,
  isLoading,
  activeView,
  onEdit,
  onDelete,
  onRestore,
  onRowClick,
}: AircraftTableProps) {
  const navigate = useNavigate()
  const { locationSlug } = useAppContext()
  const { groupBy, search, setSearch } = useAircraftsRegistryStore()

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const handleViewOnMap = (aircraftId: string) => {
    if (locationSlug) {
      navigate(`/${locationSlug}/ramp?aircraft=${aircraftId}`)
    }
  }

  // Build columns: 6 always-on + contextual per view + actions
  const columns = useMemo((): ColumnDef<AircraftTableRow>[] => {
    const config = getSavedViewConfig(activeView)

    const alwaysOnColumns: ColumnDef<AircraftTableRow>[] = [
      // 1. Tail Number (clickable)
      {
        accessorKey: 'tail_number',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Tail Number" />
        ),
        cell: ({ row }) => {
          const isDeleted = !!(row.original as ExtendedRow).deleted_at
          return (
            <button
              className={cn(
                'font-mono font-medium text-left hover:underline text-primary',
                isDeleted && 'line-through text-muted-foreground'
              )}
              onClick={(e) => {
                e.stopPropagation()
                onRowClick(row.original.id)
              }}
            >
              {row.getValue('tail_number')}
            </button>
          )
        },
        enableHiding: false,
      },
      // 2. Type + Category icon
      {
        id: 'type',
        accessorFn: (row) => {
          const r = row as ExtendedRow
          return r.aircraft_type_name || (r.manufacturer && r.model ? `${r.manufacturer} ${r.model}` : null)
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Type" />
        ),
        cell: ({ row }) => {
          const typeName = row.original.aircraft_type_name
          const manufacturer = row.original.manufacturer || row.original.aircraft_type_manufacturer
          const model = row.original.model || row.original.aircraft_type_model
          const category = row.original.aircraft_type_category ?? null

          const label = typeName || (manufacturer && model ? `${manufacturer} ${model}` : null)

          if (!label) {
            return <span className="text-muted-foreground">—</span>
          }

          return (
            <div className="flex items-center gap-1.5">
              <CategoryIcon category={category} />
              <span className="text-sm">{label}</span>
            </div>
          )
        },
      },
      // 3. Owner/Operator (clickable → clients page)
      {
        id: 'owner',
        accessorFn: (row) => {
          const r = row as ExtendedRow
          return r.owner_client_name || r.owner_name || null
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Owner" />
        ),
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const ownerClient = original.owner_client_name
          const ownerName = original.owner_name
          const displayName = ownerClient || ownerName

          if (!displayName) {
            return <span className="text-muted-foreground">—</span>
          }

          return (
            <div>
              <button
                className="font-medium text-left hover:underline text-primary"
                onClick={(e) => {
                  e.stopPropagation()
                  if (original.owner_client_id) {
                    navigate(locationSlug
                      ? `/${locationSlug}/clients?search=${encodeURIComponent(displayName)}`
                      : `/clients?search=${encodeURIComponent(displayName)}`)
                  }
                }}
              >
                {displayName}
              </button>
              {original.operator_client_name && original.operator_client_name !== ownerClient && (
                <div className="text-xs text-muted-foreground">
                  Opr: {original.operator_client_name}
                </div>
              )}
            </div>
          )
        },
      },
      // 4. Presence badge
      {
        id: 'presence',
        accessorFn: (row) => (row as ExtendedRow).presence ?? 'away',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Presence" />
        ),
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const presence = original.presence ?? 'away'
          const option = PRESENCE_OPTIONS.find((o) => o.value === presence)

          const variantMap: Record<string, 'default' | 'secondary' | 'outline'> = {
            on_field: 'default',
            arriving: 'secondary',
            departing: 'secondary',
            away: 'outline',
          }

          return (
            <Badge variant={variantMap[presence] ?? 'outline'} className="text-xs">
              {option?.icon && <span className="mr-1">{option.icon}</span>}
              {option?.label ?? presence}
            </Badge>
          )
        },
      },
      // 5. Relationship badge
      {
        id: 'relationship',
        accessorFn: (row) => (row as ExtendedRow).relationship ?? 'unknown',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Relationship" />
        ),
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const rel = original.relationship ?? 'unknown'
          const label = RELATIONSHIP_LABELS[rel as AircraftRelationship] ?? rel

          const variantMap: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
            based: 'default',
            transient: 'secondary',
            unknown: 'outline',
          }

          return (
            <Badge variant={variantMap[rel] ?? 'outline'} className="text-xs">
              {label}
            </Badge>
          )
        },
      },
      // 6. Current Location (building/unit) — clickable → ramp
      {
        id: 'current_location',
        header: 'Location',
        cell: ({ row }) => {
          const original = row.original as ExtendedRow
          const building = original.building_name
          const unit = original.unit_name
          const homeLoc = original.home_location_name

          // Show on-field position if available, otherwise home location
          const display = building
            ? unit ? `${building} / ${unit}` : building
            : homeLoc ?? null

          if (!display) {
            return <span className="text-muted-foreground">—</span>
          }

          // If on-field (has building), clicking navigates to ramp
          if (building && locationSlug) {
            return (
              <button
                className="text-sm text-left hover:underline text-primary"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/${locationSlug}/ramp?aircraft=${row.original.id}`)
                }}
              >
                {display}
              </button>
            )
          }

          return <span className="text-sm">{display}</span>
        },
      },
    ]

    // Build contextual columns from saved view config
    const contextualCols = config.contextualColumns.map(buildContextualColumn)

    // Actions column
    const actionsColumn: ColumnDef<AircraftTableRow> = {
      id: 'actions',
      cell: ({ row }) => {
        const isDeleted = !!(row.original as ExtendedRow).deleted_at
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {isDeleted ? (
                <DropdownMenuItem onClick={() => onRestore?.(row.original.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <>
                  {locationSlug && (
                    <>
                      <DropdownMenuItem onClick={() => handleViewOnMap(row.original.id)}>
                        <MapIcon className="mr-2 h-4 w-4" />
                        View on Map
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.id, row.original.tail_number)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    }

    return [...alwaysOnColumns, ...contextualCols, actionsColumn]
  }, [activeView, locationSlug, navigate, onRowClick, onEdit, onDelete, onRestore])

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const s = filterValue.toLowerCase()
      const original = row.original as ExtendedRow
      const tailNumber = (original.tail_number || '').toLowerCase()
      const typeName = (original.aircraft_type_name || '').toLowerCase()
      const manufacturer = (original.manufacturer || '').toLowerCase()
      const model = (original.model || '').toLowerCase()
      const owner = (original.owner_client_name || original.owner_name || '').toLowerCase()
      const operator = (original.operator_client_name || '').toLowerCase()

      return (
        tailNumber.includes(s) ||
        typeName.includes(s) ||
        manufacturer.includes(s) ||
        model.includes(s) ||
        owner.includes(s) ||
        operator.includes(s)
      )
    },
    state: {
      sorting,
      columnFilters,
      globalFilter: search,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  })

  // Group rows by selected option
  const groupedRows = useMemo(() => {
    const filteredRows = table.getRowModel().rows
    return groupRows(filteredRows, groupBy)
  }, [table.getRowModel().rows, groupBy])

  return (
    <div className="flex flex-col h-full">
      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading aircraft...
                  </div>
                </TableCell>
              </TableRow>
            ) : groupedRows.size === 0 || (groupedRows.size === 1 && groupedRows.get('all')?.length === 0) ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No aircraft found. Add aircraft to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              Array.from(groupedRows.entries()).map(([groupKey, rows]) => (
                <GroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  groupBy={groupBy}
                  rows={rows}
                  colSpan={table.getVisibleFlatColumns().length}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — pinned to bottom */}
      <div className="flex-shrink-0">
        <DataTablePagination table={table} />
      </div>
    </div>
  )
}

// ============================================
// Group Section
// ============================================

function GroupSection({
  groupKey,
  groupBy,
  rows,
  colSpan,
  onRowClick,
}: {
  groupKey: string
  groupBy: GroupingOption
  rows: Row<AircraftTableRow>[]
  colSpan: number
  onRowClick: (id: string) => void
}) {
  if (groupBy === 'none' || groupKey === 'all') {
    return (
      <>
        {rows.map((row) => {
          const isDeleted = !!(row.original as ExtendedRow).deleted_at
          return (
            <TableRow
              key={row.id}
              className={cn('cursor-pointer hover:bg-muted/50', isDeleted && 'opacity-50')}
              onClick={() => onRowClick(row.original.id)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </>
    )
  }

  const { label, emoji } = getGroupLabel(groupKey, groupBy)

  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={colSpan} className="py-2">
          <div className="flex items-center gap-2 font-medium">
            {emoji && <span>{emoji}</span>}
            <span>{label}</span>
            <span className="text-muted-foreground font-normal">
              ({rows.length})
            </span>
          </div>
        </TableCell>
      </TableRow>
      {rows.map((row) => {
        const isDeleted = !!(row.original as ExtendedRow).deleted_at
        return (
          <TableRow
            key={row.id}
            className={cn('cursor-pointer hover:bg-muted/50', isDeleted && 'opacity-50')}
            onClick={() => onRowClick(row.original.id)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                )}
              </TableCell>
            ))}
          </TableRow>
        )
      })}
    </>
  )
}
